<template>
  <v-main class="bg-gradient-to-br from-blue-50 to-indigo-50" style="min-height: calc(100vh - 64px);">
    <v-container fluid class="pa-0">
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3" class="mb-0" style="border-radius: 16px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); overflow: hidden;">
            <!-- Page Header -->
            <v-card-title class="pb-2 pt-3 px-4" style="background: rgba(255,255,255,0.1);">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h6 font-weight-bold white--text mb-0">
                    <v-icon left color="white" size="24">mdi-account-group</v-icon>
                    Role Management
                  </h2>
                </v-col>
                <v-col cols="12" md="4" class="pa-0 text-right">
                  <v-btn
                    :to="'/roles/create'"
                    color="white"
                    class="text-primary font-weight-bold"
                    elevation="2"
                    style="border-radius: 25px; text-transform: none; padding: 0 24px;"
                  >
                    <v-icon left size="20">mdi-plus-circle</v-icon>
                    Add New Role
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Advanced Filtration Section -->
            <v-card-text class="px-4 pb-4" style="background: rgba(255,255,255,0.1);">
              <v-row class="filter-row" align="center">
                <v-col cols="12" md="4" lg="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    placeholder="Search roles..."
                    prepend-inner-icon="mdi-magnify"
                    color="white"
                    class="white-input compact-input"
                    hide-details
                    clearable
                    solo
                    style="border-radius: 25px;"
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  >
                    <template v-slot:label>
                      <span style="color: rgba(255,255,255,0.8); font-size: 14px;">Search roles...</span>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="3" lg="2">
                  <v-select
                    v-model="filter.is_active"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    placeholder="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    color="white"
                    class="white-input compact-input"
                    hide-details
                    clearable
                    solo
                    style="border-radius: 25px;"
                    @click:clear="(filter.is_active = null), (page = 1), getData()"
                    @input="getData"
                  >
                    <template v-slot:label>
                      <span style="color: rgba(255,255,255,0.8); font-size: 14px;">Status</span>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" md="5" lg="7" class="text-right">
                  <div class="filter-info">
                    <v-chip
                      color="rgba(255,255,255,0.2)"
                      text-color="white"
                      small
                      class="ma-1"
                      style="border-radius: 16px;"
                    >
                      <v-icon left size="16">mdi-database</v-icon>
                      Total: {{ count || 0 }}
                    </v-chip>
                    <v-chip
                      color="rgba(255,255,255,0.2)"
                      text-color="white"
                      small
                      class="ma-1"
                      style="border-radius: 16px;"
                    >
                      <v-icon left size="16">mdi-file-document</v-icon>
                      Page: {{ page }}/{{ paginationLength || 1 }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" style="border-radius: 16px; overflow: hidden; background: white;">
            <v-data-table
              :headers="headers"
              :items="roles"
              :loading="isLoading"
              :page.sync="page"
              :server-items-length="count"
              hide-default-footer
              :height="calculateTableHeight()"
              fixed-header
              class="modern-table"
              :class="{ 'table-loading': isLoading }"
            >
              <!-- Loading State -->
              <template v-slot:progress>
                <v-progress-linear
                  indeterminate
                  color="primary"
                  height="3"
                ></v-progress-linear>
              </template>

              <!-- Serial Number Column -->
              <template v-slot:[`item.serial`]="{ index }">
                <span class="font-weight-medium text-primary">{{ (page - 1) * rowsPerPage + index + 1 }}</span>
              </template>

              <!-- Status Column -->
              <template v-slot:[`item.is_active`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      v-on="on"
                      :color="item.is_active == 1 ? 'success' : 'error'"
                      :text-color="'white'"
                      small
                      class="ma-1"
                      style="border-radius: 12px; font-weight: 600;"
                    >
                      <v-icon left size="14">
                        {{ item.is_active == 1 ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                      {{ item.is_active == 1 ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </template>
                  <span>Role is {{ item.is_active == 1 ? 'Active' : 'Inactive' }}</span>
                </v-tooltip>
              </template>

              <!-- Role Name Column -->
              <template v-slot:[`item.name`]="{ item }">
                <div class="role-info">
                  <span class="font-weight-medium">{{ item.name || '-' }}</span>
                </div>
              </template>

              <!-- Actions Column -->
              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        :to="`/roles/${encryptIt(item.id)}`"
                        color="primary"
                        small
                        icon
                        class="ma-1"
                        style="border-radius: 8px;"
                      >
                        <v-icon size="16">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit Role</span>
                  </v-tooltip>
                </div>
              </template>

              <!-- Empty State -->
              <template v-slot:no-data>
                <empty-state icon="mdi-account-group-outline" title="No Roles Found" message="There are no roles matching your criteria.">
                  <v-btn
                  :to="'/roles/create'"
                  color="primary"
                  style="border-radius: 25px; text-transform: none;"
                  >
                  <v-icon left>mdi-plus</v-icon>
                  Add First Role
                  </v-btn>
                </empty-state>
              </template>
            </v-data-table>

            <!-- Integrated Pagination -->
            <v-divider></v-divider>
            <div class="px-4 py-3" style="background: #fafafa;">
              <v-row align="center" class="ma-0">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="pagination-info">
                    <span class="text-sm grey--text">
                      Showing {{ (page - 1) * rowsPerPage + 1 }} to {{ Math.min(page * rowsPerPage, count) }} of {{ count }} entries
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0 text-right">
                  <v-pagination
                    v-if="paginationLength > 1"
                    v-model="page"
                    :length="paginationLength"
                    :total-visible="7"
                    color="primary"
                    class="custom-pagination"
                  ></v-pagination>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="deleteLoading"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
export default {
  name: "rolesPage",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
      },
      roles: [],
      headers: [
        {
          text: 'Sr No',
          value: 'serial',
          sortable: false,
          width: '80px',
          class: 'text-primary font-weight-bold',
          cellClass: 'text-center'
        },
        {
          text: 'Status',
          value: 'is_active',
          sortable: false,
          width: '120px',
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Role Name',
          value: 'name',
          sortable: false,
          class: 'text-primary font-weight-bold'
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: '100px',
          align: 'center',
          class: 'text-primary font-weight-bold'
        }
      ],
      statuses: [
        {
          id: 0,
          text: "All Status",
          value: "",
        },
        { id: "ACTIVE", text: "Active", value: "1" },
        { id: "INACTIVE", text: "Inactive", value: "0" },
      ],
      ExportRoleTypes: [],
      page: 1,
      rowsPerPage: 15,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      excel_fields: {
        "Sr No": "sr_no",
        Name: "name",
        Price: "price",
      },
      isLoading: false,
      isExcelloading: false,
      delete_role: false,
      is_deleted_role: false,
      deleteDialog: false,
      itemToDelete: null,
      deleteLoading: false,
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    calculateTableHeight() {
      return Math.max(400, window.innerHeight - 320);
    },
    async getData() {
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.is_active = this.filter.is_active ? this.filter.is_active : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&is_active=" +
        this.filter.is_active +
        "&search_keyword=" +
        this.filter.search_keyword;
      await axios
        .get(`roles?${query}`)
        .then((response) => {
          this.roles = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
      this.isLoading = false;
    },
    //Generate Excel
    async generateExcel() {
      let query = "search_keyword=" + this.filter.search_keyword;
      let response = await axios.get(`role_exports?${query}`);
      console.log(response.data.data);
      if (response) {
        window.open(`${response.data.data}`, "_blank");
      }
    },
    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/roles/delete/${this.encryptIt(this.itemToDelete.id)}`);
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.deleteLoading = false;
    },
    async Delete_this(id) {
      this.delete_role = true;
      await axios
        .post(`/roles/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_role = false;
          this.is_deleted_role = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
  },
};
</script>

<style scoped>
.bg-gradient-to-br {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: calc(100vh - 64px);
}

.white-input >>> .v-input__control .v-input__slot {
  background-color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 25px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.white-input >>> .v-input__control .v-input__slot:before,
.white-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.white-input >>> .v-text-field__slot input,
.white-input >>> .v-select__slot input {
  color: #2c3e50 !important;
  font-weight: 500;
}

.white-input >>> .v-text-field__slot input::placeholder,
.white-input >>> .v-select__slot input::placeholder {
  color: #7f8c8d !important;
}

.white-input >>> .v-icon {
  color: #34495e !important;
}

.compact-input >>> .v-input__control {
  min-height: 44px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 44px !important;
  padding: 0 16px !important;
}

.filter-row .v-col {
  padding: 4px 8px;
}

.filter-info .v-chip {
  margin: 2px 4px;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0;
  box-shadow: none;
}

.modern-table >>> .v-data-table > .v-data-table__wrapper > table {
  border-spacing: 0;
}

.modern-table >>> .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 16px !important;
  font-size: 14px !important;
  border: none !important;
  box-shadow: none !important;
}

.modern-table >>> .v-data-table > .v-data-table__wrapper > table > tbody > tr {
  transition: all 0.2s ease;
}

.modern-table >>> .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background: #f8f9ff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.modern-table >>> .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 16px !important;
  border-bottom: 1px solid #e9ecef !important;
  vertical-align: middle;
}

.modern-table >>> .v-data-table > .v-data-table__wrapper > table > tbody > tr:last-child > td {
  border-bottom: none !important;
}

.table-loading >>> .v-data-table__wrapper {
  opacity: 0.6;
  pointer-events: none;
}

.role-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
  font-weight: 500;
}

.custom-pagination >>> .v-pagination__item {
  box-shadow: none;
  border: 1px solid #e9ecef;
  background: white;
  color: #495057;
  font-weight: 500;
  min-width: 36px;
  height: 36px;
  margin: 0 2px;
}

.custom-pagination >>> .v-pagination__item--active {
  background: #667eea !important;
  color: white !important;
  border-color: #667eea !important;
}

.custom-pagination >>> .v-pagination__navigation {
  box-shadow: none;
  border: 1px solid #e9ecef;
  background: white;
  color: #495057;
  min-width: 36px;
  height: 36px;
  margin: 0 2px;
}

.custom-pagination >>> .v-pagination__navigation:hover,
.custom-pagination >>> .v-pagination__item:hover {
  background: #f8f9fa !important;
  border-color: #dee2e6 !important;
}

.custom-pagination >>> .v-pagination__navigation--disabled {
  opacity: 0.5;
  pointer-events: none;
}

@media (max-width: 960px) {
  .filter-row .v-col {
    margin-bottom: 8px;
  }
  
  .filter-info {
    text-align: left !important;
    margin-top: 8px;
  }
  
  .action-buttons {
    justify-content: flex-end;
  }
}

@media (max-width: 600px) {
  .modern-table >>> .v-data-table > .v-data-table__wrapper > table > thead > tr > th,
  .modern-table >>> .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    padding: 12px 8px !important;
    font-size: 12px !important;
  }
  
  .pagination-info {
    font-size: 12px;
    margin-bottom: 8px;
  }
  
  .custom-pagination {
    justify-content: center;
  }
}
</style>
